import React from 'react';
import { TextV2, Box, ButtonV2, DialogV2, CancelButton } from '@withjoy/joykit';
import { StyledFlexFooter } from './CurrencyDialog.styles';
import { CurrencySelectorV2 } from '@shared/components/CurrencySelectorV2';
import { useTranslation } from '@shared/core';
import { v1 as uuid } from 'uuid';
import { Divider } from '@apps/registry/common/components';
import { useCurrencyDialogController } from './CurrencyDialog.controller';
import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useCurrencyLockMessage } from '../useCurrencyLockMessage';

const OVERLAY_KEY = `currency-picker-${uuid()}`;
const LOCKED_MESSAGE_ID = 'currency-dialog-locked-message';

export type CurrencyDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  eventId: Maybe<string>;
  registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  setRegistryCurrency?: (c: RegistryCurrencyCodesFragment) => void;
  isLocked?: boolean;
}>;

export const CurrencyDialog: React.FC<CurrencyDialogProps> = ({ isOpen, onClose, eventId, registryCurrency, setRegistryCurrency, isLocked }) => {
  const { onSave, onCancel, currencies, selectedCurrencyCode, setSelectedCurrencyCode } = useCurrencyDialogController({
    onClose,
    eventId,
    registryCurrency,
    setRegistryCurrency,
    isLocked,
  });
  const { t } = useTranslation('yourRegistry');
  const lockedCurrencyCode = isLocked ? (registryCurrency?.code ?? DEFAULT_CURRENCY) : null;
  const lockMessage = useCurrencyLockMessage(lockedCurrencyCode);
  const { currencyDialogTitle, currencyDialogSubtitle, currencyDialogSecondarySubtitle, currencyDialogThirdSubtitle, currencyDialogActionButton, currencyDialogSecondaryButton } =
    t('currency');

  return (
    <DialogV2
      id={OVERLAY_KEY}
      onClose={onClose}
      isOpen={isOpen}
      size="lg"
    >
      <DialogV2.Body>
        <TextV2
          textAlign="left"
          letterSpacing="-1%"
          fontWeight={600}
          fontStyle="normal"
          fontSize={17}
          component="h1"
          marginBottom={2}
        >
          {currencyDialogTitle()}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={6}
          typographyVariant="body1"
        >
          {currencyDialogSubtitle()}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={6}
          typographyVariant="body1"
        >
          {currencyDialogSecondarySubtitle()}
        </TextV2>
        <TextV2
          component="p"
          marginBottom={isLocked ? 6 : 54}
          typographyVariant="body1"
        >
          {currencyDialogThirdSubtitle()}
        </TextV2>
        {lockMessage && (
          <TextV2
            id={LOCKED_MESSAGE_ID}
            component="p"
            marginBottom={6}
            typographyVariant="body1"
            color="mono10"
          >
            {lockMessage}
          </TextV2>
        )}
        <Box marginBottom={7}>
          <CurrencySelectorV2
            value={selectedCurrencyCode}
            onChange={setSelectedCurrencyCode}
            currencies={currencies}
            disabled={isLocked}
            searchable
            {...(isLocked && { 'aria-describedby': LOCKED_MESSAGE_ID })}
          />
        </Box>
      </DialogV2.Body>
      <Divider margin="0px" />
      <DialogV2.Footer>
        <StyledFlexFooter
          justifyContent="flex-end"
          flexDirection={['column-reverse', 'row']}
          width="100%"
        >
          <CancelButton
            onClick={onCancel}
            width={['100%', '132px']}
          >
            {currencyDialogSecondaryButton()}
          </CancelButton>
          {!isLocked && (
            <ButtonV2
              intent="primary"
              onClick={onSave}
              width={['100%', '132px']}
            >
              {currencyDialogActionButton()}
            </ButtonV2>
          )}
        </StyledFlexFooter>
      </DialogV2.Footer>
    </DialogV2>
  );
};
